import React, {useMemo} from "react";
import {Field, Form} from "react-final-form";
import Skeleton from "react-loading-skeleton";
import Input from "../../common/components/elements/input/Input";
import RowLayout from "../../common/components/elements/container/row";
import ColumnLayout from "../../common/components/elements/container/column";
import {FormContainer, FormFields} from "../../common/components/collections/form/forms";
import {PrimaryLoadingButton} from "../../common/components/elements/button/PrimaryButton";
import {FormText} from "../../common/components/collections/form/forms";
import styled from "styled-components";
import FileField from "../../common/components/elements/file/FileField";
import curry from "lodash/curry";
import get from "lodash/get";
import {composeValidators, required, validateSize} from "../../common/validations";
import Error from "../../common/components/elements/Error";
import {imageFileValidator} from "../../common/components/elements/file/FileUpload";
import PhoneInputField from "../../common/components/elements/input/PhoneInput";
import DatePicker from "../../common/components/elements/date-picker/DatePicker";
import {yearsAgo} from "src/core/common/utils";
import useFormsCache from "src/core/common/hooks/useFormsCache";
import useAgeValidation from "../../authentication/hooks/useAgeValidation";
import useFeatureToggles from "../../common/hooks/useFeatureToggles";
import useUserProfile from "src/core/authentication/hooks/useUserProfile";
import useDeliveryType from "../../deliveries/hooks/useDeliveryType";
import MarketingOptInField from "../../authentication/components/MarketingOptInField";
import useSite from "../../sites/hooks/useSite";
import LockerIcon from "../../common/assets/locker.svg";
import {DeliveryTypes} from "../../common/models/DeliveryType";
import useDocuments from "../hooks/useDocuments";
import MedicalInfoForm from "src/core/authentication/onboarding/express/MedicalInfoForm";
import {IdVerificationContext} from "src/integrations/identityVerification/components/IdentityVerificationContext";
import {ErrorTags} from "src/core/api/errorHandlers";
import {showErrorHighlight} from "src/core/common/utils";
import IdPlaceholder from "src/core/user/assets/id-placeholder.svg";
import MedicalPlaceholder from "src/core/user/assets/medical-placeholder.svg";
import SelfiePlaceholder from "src/core/user/assets/selfie-placeholder.svg";
import {useMediaQuery} from "react-responsive";
import useInventoryType from "src/core/inventories/useInventoryType";
import DriversLicenseIDFields from "src/core/authentication/components/DriversLicenseIDFields";
import StateSelect from "src/core/authentication/components/StateSelect";
import DocumentsIdentityVerificationLabel from "src/integrations/identityVerification/components/DocumentsIdentityVerificationLabel";
import PersonalInfoSection from "src/core/user/components/PersonalInfoSection";

function PersonalInfoSkeleton() {
  const isMobile = useMediaQuery({maxWidth: 767});

  return (
    <FormContainer>
      <CustomColumnLayout>
        <div style={{marginRight: "8px", flex: 1}}>
          <Skeleton height={130} width={isMobile ? undefined : 144} />
        </div>
        <div style={{flex: 1}}>
          <Skeleton height={130} width={isMobile ? undefined : 144} />
        </div>
        <div style={{marginLeft: "8px", flex: 1}}>
          <Skeleton height={130} width={isMobile ? undefined : 144} />
        </div>
      </CustomColumnLayout>
      <ColumnLayout>
        <SkeletonContainer>
          <Skeleton height={25} width={280} />
        </SkeletonContainer>
      </ColumnLayout>
      <ColumnLayout>
        <SkeletonContainer>
          <Skeleton height={48} />
        </SkeletonContainer>
      </ColumnLayout>
      <ColumnLayout>
        <SkeletonContainer>
          <Skeleton height={48} />
        </SkeletonContainer>
      </ColumnLayout>
      <ColumnLayout>
        <SkeletonContainer>
          <Skeleton height={48} />
        </SkeletonContainer>
      </ColumnLayout>
      <ColumnLayout>
        <SkeletonContainer>
          <Skeleton height={48} />
        </SkeletonContainer>
      </ColumnLayout>
      <ColumnLayout>
        <SkeletonContainer>
          <Skeleton height={25} width={280} />
        </SkeletonContainer>
      </ColumnLayout>
      <ColumnLayout>
        <SkeletonContainer>
          <Skeleton height={25} width={280} />
        </SkeletonContainer>
      </ColumnLayout>
      <ColumnLayout>
        <SkeletonContainer>
          <Skeleton height={48} />
        </SkeletonContainer>
      </ColumnLayout>
    </FormContainer>
  );
}

function PersonalInfoForm({builder, onFinish, errorTag, verificationStatus, ...props}) {
  const [profileState, authApi] = useUserProfile();
  const user = authApi.userProfile();
  const [setEmailValue] = useFormsCache(FormFields.EMAIL);
  const [phoneValue, setPhoneValue] = useFormsCache(FormFields.PHONE);
  const [dateOfBirthValue, setDateOfBirthValue] = useFormsCache(FormFields.DATE_OF_BIRTH);
  const [stateResidencyValue, setStateResidencyValue] = useFormsCache(
    FormFields.STATE_RESIDENCY
  );
  const [minimumAgeDate, age] = useAgeValidation();
  const toggles = useFeatureToggles();

  function onSubmit(formValues) {
    builder
      .setFirstName(formValues.firstName)
      .setLastName(formValues.lastName)
      .setEmail(formValues.email)
      .setDateOfBirth(formValues.dateOfBirth)
      .setPhoneNumber(formValues.phoneNumber)
      .setMedicalId(get(formValues.medicalId, "0", undefined))
      .setMedicalIdNumber(formValues.medicalIdNumber)
      .setMedicalIdExpirationDate(formValues.medicalIdExpirationDate)
      .setMedicalIdState(formValues.medicalIdState)
      .setIdentityDocument(get(formValues.identityDocument, "0", undefined))
      .setSelfieId(get(formValues.selfieId, "0", undefined))
      .setMarketingEmailOptIn(formValues.marketingEmailOptIn)
      .setMarketingSmsOptIn(formValues.marketingSmsOptIn)
      .setStateResidency(formValues.stateResidency)
      .setDriversLicenseIdNumber(formValues.driversLicenseIdNumber)
      .setDriversLicenseIdExpirationDate(formValues.driversLicenseIdExpirationDate);
    onFinish();
  }

  const onChangeEmail = curry((callback, evt) => {
    const value = evt.target.value;
    setEmailValue(value);
    callback(value);
  });

  const onChangePhone = curry((callback, evt) => {
    const value = evt.target.value;
    setPhoneValue(value);
    callback(value);
  });

  const onChangeDateOfBirth = curry((callback, value) => {
    setDateOfBirthValue(value);
    callback(value);
  });

  const initialValues = useMemo(() => {
    if (!user) return {};

    return {
      firstName: user.getFirstName(),
      lastName: user.getLastName(),
      email: user.getEmail(),
      phoneNumber: user.getPhoneNumber(),
      dateOfBirth: user.getDateOfBirth(),
      marketingSmsOptIn: user.getMarketingSmsOptIn(),
      marketingEmailOptIn: user.getMarketingEmailOptIn(),
      medicalIdNumber: user.getMedicalIdNumber(),
      medicalIdExpirationDate: user.getMedicalIdExpirationDate(),
      medicalIdState: user.getMedicalIdState(),
      driversLicenseIdNumber: user.getDriversLicenseIdNumber(),
      driversLicenseIdExpirationDate: user.getDriversLicenseIdExpirationDate(),
      stateResidency: user.getStateResidency(),
    };
  }, [user]);

  const [deliveryType] = useDeliveryType();
  const showSelfieIdUpload =
    deliveryType &&
    ((deliveryType.code === DeliveryTypes.DELIVERY &&
      !toggles.deliveryCheckoutSelfieIdHidden()) ||
      (deliveryType.code === DeliveryTypes.PICK_UP &&
        !toggles.pickupCheckoutSelfieIdHidden()));

  function computePhoneInputProps({input}) {
    return {
      disabled: true,
      onChange: onChangePhone(input.onChange),
    };
  }

  const displayMedicalIdFields = toggles.displayMedicalIdFields();

  return (
    <>
      {authApi.isValidating() && <PersonalInfoSkeleton />}
      {!authApi.isValidating() && (
        <Form
          onSubmit={onSubmit}
          validate={() => {}}
          initialValues={initialValues}
          render={({handleSubmit}) => (
            <form onSubmit={handleSubmit} noValidate={true} id={"personal-info-section"}>
              <FormContainer>
                {toggles.allowDocumentsOnPersonalInformation() && (
                  <DocumentsSection
                    user={user}
                    showSelfieIdUpload={showSelfieIdUpload}
                    hasErrors={props.hasErrors && errorTag === ErrorTags.DOCUMENTS}
                    verificationStatus={verificationStatus}
                  />
                )}
                <ColumnLayout>
                  <CustomCheckoutPersonalInfoSection
                    user={user}
                    showAsDisabled={true}
                    disabled={toggles.allowNameAndDobUpdate()}
                  />
                </ColumnLayout>
                {user && user.getEmail() && (
                  <ColumnLayout>
                    <RowLayout>
                      <Field
                        name="email"
                        initialValue={user.getEmail()}
                        render={({input, meta}) => (
                          <>
                            <Input
                              {...input}
                              disabled={true}
                              hasErrors={showErrorHighlight(meta)}
                              placeholder={"Email"}
                              onChange={onChangeEmail(input.onChange)}
                            />
                            {meta.touched && <Error>{meta.error}</Error>}
                          </>
                        )}
                      />
                    </RowLayout>
                  </ColumnLayout>
                )}

                {user && user.getPhoneNumber() && (
                  <ColumnLayout>
                    <RowLayout>
                      <PhoneInputField
                        name={"phoneNumber"}
                        initialValue={phoneValue}
                        computePhoneInputProps={computePhoneInputProps}
                      />
                    </RowLayout>
                  </ColumnLayout>
                )}
                {toggles.registrationWithDriversLicenseID() && (
                  <ColumnLayout>
                    <RowLayout>
                      <DriversLicenseIDFields disabled={true} />
                    </RowLayout>
                  </ColumnLayout>
                )}
                <ColumnLayout>
                  <RowLayout>
                    <DatePicker
                      name={"dateOfBirth"}
                      placeholder={"Date of Birth"}
                      initialValue={initialValues.dateOfBirth || dateOfBirthValue}
                      maxDate={minimumAgeDate}
                      minDate={yearsAgo(150)}
                      validate={composeValidators(required, age)}
                      onChange={onChangeDateOfBirth}
                      disabled={toggles.allowNameAndDobUpdate()}
                    />
                  </RowLayout>
                </ColumnLayout>

                {toggles.displayStateResidencyOnSignUp() && (
                  <ColumnLayout>
                    <RowLayout>
                      <StateSelect
                        name={"stateResidency"}
                        defaultState={stateResidencyValue}
                        onChange={setStateResidencyValue}
                        placeholder={"Residency State"}
                        disabled={true}
                        formatSelection={entry => `I am a resident of ${entry.label}`}
                      />
                    </RowLayout>
                  </ColumnLayout>
                )}

                {displayMedicalIdFields && (
                  <MedicalInfoForm
                    title={"Medical Information"}
                    hasErrors={props.hasErrors && errorTag === ErrorTags.DOCUMENTS}
                  />
                )}

                <ColumnLayout>
                  <RowLayout>
                    <MarketingOptInField
                      requestEmail={user && user.getEmail()}
                      requestPhone={user && user.getPhoneNumber()}
                      hasErrors={props.hasErrors && errorTag === "marketing"}
                    />
                  </RowLayout>
                </ColumnLayout>
                <ColumnLayout>
                  <PrimaryLoadingButton
                    loading={profileState.loading}
                    label="Save"
                    type="submit"
                  />
                </ColumnLayout>
              </FormContainer>
            </form>
          )}
        />
      )}
    </>
  );
}

function DocumentsSectionSkeleton() {
  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: "16px",
        }}
      >
        <DocumentSkeletonContainer>
          <Skeleton height={130} />
        </DocumentSkeletonContainer>
        <DocumentSkeletonContainer>
          <Skeleton height={130} />
        </DocumentSkeletonContainer>
        <DocumentSkeletonContainer>
          <Skeleton height={130} />
        </DocumentSkeletonContainer>
      </div>
      <div style={{marginTop: "8px", marginBottom: "16px"}}>
        <Skeleton height={50} />
      </div>
    </>
  );
}

function DocumentsSection({user, showSelfieIdUpload, hasErrors}) {
  const site = useSite();
  const toggles = useFeatureToggles();
  const personalInfoDisclaimer = site.getOptions().getPersonalInfoDisclaimer();
  const sanitizePersonalInfoDisclaimer = () => ({__html: personalInfoDisclaimer});
  const [deliveryType] = useDeliveryType();
  const [inventoryType] = useInventoryType();

  const driversLicense = useDocuments(
    user.hasDriversLicense(deliveryType.code),
    "drivers-license"
  );
  const medicalId = useDocuments(user.hasMedicalId(), "medical-id");
  const selfieId = useDocuments(user.hasSelfieId(deliveryType.code), "selfie-id");

  const constraints = site
    .getOptions()
    .checkoutDocumentConstraints({inventoryType, deliveryType});
  const dlConstraint = constraints.find(c => c.type() === "drivers_license");
  const medicalConstraint = constraints.find(c => c.type() === "medical_id");
  const selfieConstraint = constraints.find(c => c.type() === "selfie_id");
  const dlErrors = dlConstraint.validate(user, toggles, deliveryType.code);
  const medicalErrors = medicalConstraint.validate(user, toggles, deliveryType.code);
  const selfieErrors = selfieConstraint.validate(user, toggles, deliveryType.code);

  const displayMedicalId = toggles.displayMedicalIdDocument();

  if (driversLicense.meta.loading || medicalId.meta.loading || selfieId.meta.loading) {
    return <DocumentsSectionSkeleton />;
  }

  const fileSizeValidator = validateSize(10);
  const fileSizeAndRequiredValidator = composeValidators(required, validateSize(10));

  const getValidator = errorsList =>
    errorsList.length > 0 && hasErrors ? fileSizeAndRequiredValidator : fileSizeValidator;

  return (
    <>
      <IdVerificationContext.Consumer>
        {value => {
          const {idVerification} = value;

          const driversLicenseDocument = get(driversLicense, "document");
          const selfieIdDocument = get(selfieId, "document");

          const IdVerification = props => (
            <DocumentsIdentityVerificationLabel
              deliveryType={deliveryType}
              verificationData={value}
              {...props}
            />
          );

          return (
            <CustomColumnLayout>
              <DocumentFileUpload
                image={driversLicenseDocument}
                initialImage={driversLicenseDocument}
                hasDocs={driversLicenseDocument}
                Placeholder={IdPlaceholder}
                validate={getValidator(dlErrors)}
                fileValidator={imageFileValidator}
                hasVerificationService={idVerification?.displayVerificationStatusOnUserProfile()}
                LabelComponent={IdVerification}
                name={"identityDocument"}
                label={"Add Valid Id"}
                description={`Make sure DOB clearly visible  for age verification. ${
                  dlConstraint.isRequired() ? "(Required)" : "(Optional)"
                }`}
                hasErrors={dlErrors.length > 0 && hasErrors}
              />

              {displayMedicalId && (
                <DocumentFileUpload
                  image={medicalId.document}
                  initialImage={medicalId.document}
                  Placeholder={MedicalPlaceholder}
                  fileValidator={imageFileValidator}
                  validate={getValidator(medicalErrors)}
                  name={"medicalId"}
                  label={"Add Medical Id"}
                  description={
                    medicalConstraint.isRequired() ? "(Required)" : "(Optional)"
                  }
                  hasErrors={medicalErrors.length > 0 && hasErrors}
                />
              )}
              {showSelfieIdUpload && (
                <DocumentFileUpload
                  image={selfieIdDocument}
                  initialImage={selfieIdDocument}
                  Placeholder={SelfiePlaceholder}
                  fileValidator={imageFileValidator}
                  hasVerificationService={idVerification?.displayVerificationStatusOnUserProfile()}
                  LabelComponent={IdVerification}
                  validate={getValidator(selfieErrors)}
                  name={"selfieId"}
                  label={"Add Selfie Id"}
                  description={
                    selfieConstraint.isRequired() ? "(Required)" : "(Optional)"
                  }
                  hasErrors={selfieErrors.length > 0 && hasErrors}
                />
              )}
            </CustomColumnLayout>
          );
        }}
      </IdVerificationContext.Consumer>
      <PrivacyPolicyText alignment={"center"}>
        {personalInfoDisclaimer ? (
          <div dangerouslySetInnerHTML={sanitizePersonalInfoDisclaimer()} />
        ) : (
          <>
            <img src={LockerIcon} alt={""} />
            All uploaded docs are kept confidentially.
            <span>Privacy Policy.</span>
          </>
        )}
      </PrivacyPolicyText>
    </>
  );
}

const PrivacyPolicyText = styled(FormText)`
  display: flex;
  align-items: center;
  margin-bottom: ${({theme}) => theme.v2.spacing(4)};

  > span {
    margin-left: 5px;
  }

  > img {
    margin-right: 5px;
  }
`;
const DocumentFileUpload = styled(FileField)`
  &:first-child {
    margin-right: ${({theme}) => theme.v2.spacing(2)};
  }

  &:last-child {
    margin-left: ${({theme}) => theme.v2.spacing(2)};
  }

  min-height: 130px;
`;
const DocumentSkeletonContainer = styled.div`
  width: 32%;
`;

const CustomCheckoutPersonalInfoSection = styled(PersonalInfoSection)`
  width: 100%;
  padding: 0;

  h3 {
    text-align: center;
  }
`;

const SkeletonContainer = styled.div`
  width: 100%;
`;

const CustomColumnLayout = styled(ColumnLayout)`
  justify-content: center !important;
  margin-bottom: 0 !important;
`;

export default PersonalInfoForm;
